// Optimistic staging — what a screen paints in the 120 ms before API §6.3 answers (T-125; ARCH §9.3).
//
//   const plan = optimisticStage(changeSet, request, { entity: row.entity, live, now: client.clock.now() });
//   queryClient.setQueryData(queries.changeSet(id).queryKey, ...);   // or keep `plan.markers` in local state
//   const { data } = await client.stageChange(id, request);          // the server's own item replaces it
//   await queryClient.invalidateQueries(invalidation.stageChange(id));
//
// **What is optimistic and what is not, and why the line is there.** REQ-101 AC1 asks for the edited value to
// show inline with a "staged" marker the moment the user commits it, and REQ-101 AC2 for the tray's count. Both
// of those this module answers at once, from values the screen already has: the row's live value and the value
// the user typed. What it does NOT invent is the item's SENTENCE — the consequence, the impact figures, the
// warnings and the validation errors are ENGINE §11's answers over the whole account, and a plausible-looking
// guess at "about −$22 per day" that the server then corrects is worse than a row that says nothing for 120 ms.
// So `items` is the server's list with the request applied where that needs no new facts (a merge changes a
// `to`, a removal drops a row), and a genuinely NEW item appears in the tray when the server answers.
//
// **Reconciliation is on `(entityId, field)`, never on the id.** That is the key API §6.3 upserts on, and the
// demo server mints ids from a counter it persists (`chi_7`), so a client cannot predict the id of something it
// is staging — by design: an id that could be predicted would have to be derived from the entity and the field,
// and then a removed and re-staged item would reuse the id of a decision that is over (T-126's activity log).
// `pendingItemId` is a local placeholder, prefixed so it can never be mistaken for a server id.

import { mergeItem, sameChangeValue, type MergeOutcome } from '../changeSets/index';
import type {
  ChangeField,
  ChangeItem,
  ChangeSet,
  ChangeValue,
  StageChangeRequest,
} from '../types/changes';
import type { EntityRef } from '../types/entities';
import type { IsoInstant } from '../types/primitives';

/**
 * The part of a `ChangeItem` a staged MARKER needs (`withStagedMarkers` in `@sl/core/rows` takes these).
 * `ChangeItem` satisfies it, and so does the placeholder below — which is the point: a row does not care
 * whether the item it is marked with has been to the server yet.
 */
export type StagedSource = Pick<ChangeItem, 'id' | 'entity' | 'field' | 'from' | 'to' | 'state'>;

/** The prefix of a marker the server has not answered for yet. No server id ever starts with it. */
export const PENDING_ITEM_ID_PREFIX = 'pending:';

export const pendingItemId = (entityId: string, field: ChangeField): string =>
  `${PENDING_ITEM_ID_PREFIX}${entityId}:${field}`;

export const isPendingItemId = (id: string): boolean => id.startsWith(PENDING_ITEM_ID_PREFIX);

export interface StageContext {
  /** The row the edit was made on. The server's answer carries the same ref; this one just arrives sooner. */
  entity: EntityRef;
  /** The field's value on that row right now — `entity.isOn`, or `entity.budget.amount`. */
  live: ChangeValue;
  /** `isoInstant(client.clock.now())` — never `Date.now()`, so a test's clock decides (D-020), and API §1.4's
   *  one spelling, so an optimistic marker's instant reads exactly like the server's answer (T-376). */
  now: IsoInstant;
}

export interface OptimisticStage {
  /** ENGINE §11's four answers, decided here exactly as the server decides them (`mergeItem`). */
  outcome: MergeOutcome;
  /** What every row should be marked with at once — the open set with this request applied. */
  markers: StagedSource[];
  /** The tray count for the shell pill (REQ-101 AC2) — `markers.length`. */
  count: number;
  /**
   * The items a tray that is already open can render **with their sentences**: the server's own items, minus
   * one this request removed, with a merged item's `to` moved. A newly added item is not here — it has no
   * consequence yet — and `pendingId` names it instead.
   */
  items: ChangeItem[];
  /** The placeholder id of a newly staged item, or `null` for a merge, a removal or `not_staged`. */
  pendingId: string | null;
}

const EMPTY: readonly ChangeItem[] = [];

/**
 * The open change-set as it will be once `request` lands, decided by ENGINE §11's `mergeItem` so the optimistic
 * answer and the server's cannot disagree about what an edit MEANS: a second edit of the same field updates the
 * one item and keeps its `from`; the live value removes it (REQ-101 AC3); the live value where nothing was
 * staged is `not_staged` — nothing happens and nothing is drawn (D-180 (5)).
 */
export function optimisticStage(
  current: ChangeSet | null | undefined,
  request: StageChangeRequest,
  context: StageContext,
): OptimisticStage {
  const items = current?.items ?? EMPTY;
  const staged = items.filter((it) => it.state === 'staged');
  const { outcome } = mergeItem(
    staged.map((it) => ({ entityId: it.entity.id, field: it.field, from: it.from, to: it.to })),
    request,
    context.live,
  );
  const hit = (it: StagedSource): boolean =>
    it.entity.id === request.entityId && it.field === request.field;

  if (outcome === 'not_staged') {
    return { outcome, markers: [...staged], count: staged.length, items: [...items], pendingId: null }; // prettier-ignore
  }
  if (outcome === 'removed') {
    const kept = items.filter((it) => !hit(it));
    return {
      outcome,
      markers: kept.filter((it) => it.state === 'staged'),
      count: kept.filter((it) => it.state === 'staged').length,
      items: kept,
      pendingId: null,
    };
  }
  if (outcome === 'merged') {
    // Only the value moves. Everything derived from it — the sentence, the figures, the warnings — is the
    // server's to re-answer, and the caller invalidates the moment it does.
    const next = items.map((it) => (hit(it) ? { ...it, to: request.to } : it));
    return {
      outcome,
      markers: next.filter((it) => it.state === 'staged'),
      count: next.filter((it) => it.state === 'staged').length,
      items: next,
      pendingId: null,
    };
  }
  const pending: StagedSource = {
    id: pendingItemId(request.entityId, request.field),
    entity: context.entity,
    field: request.field,
    from: context.live,
    to: request.to,
    state: 'staged',
  };
  return {
    outcome,
    markers: [...staged, pending],
    count: staged.length + 1,
    items: [...items],
    pendingId: pending.id,
  };
}

/**
 * The server's answer has landed: drop any placeholder the same `(entityId, field)` now has a real item for.
 * A caller that replaces the whole `changeSet` query with the response never needs this; one that kept
 * `markers` in local state (a list that has not refetched yet) does.
 */
export function reconcileMarkers(
  markers: readonly StagedSource[],
  server: readonly StagedSource[],
): StagedSource[] {
  const key = (it: StagedSource): string => `${it.entity.id} ${it.field}`;
  const answered = new Set(server.map(key));
  // The server is the authority for everything it has answered; a placeholder survives only while its own
  // `(entityId, field)` is still in flight. Nothing else of the local list is kept — an item the server has
  // dropped (someone discarded the tray on the other device) must disappear here too (REQ-101 AC4).
  return [...server, ...markers.filter((it) => isPendingItemId(it.id) && !answered.has(key(it)))];
}

/** REQ-101 AC3 in one call, for a control that only needs to know whether committing would UNDO a staged edit. */
export function stagingRemoves(
  current: ChangeSet | null | undefined,
  request: StageChangeRequest,
  live: ChangeValue,
): boolean {
  if (!sameChangeValue(request.to, live)) return false;
  return (current?.items ?? EMPTY).some(
    (it) =>
      it.state === 'staged' && it.entity.id === request.entityId && it.field === request.field,
  );
}
